import { useAuth0 } from "@auth0/auth0-react";
import { jwtDecode, JwtPayload } from "jwt-decode";
import { useEffect, useState } from "react";

export enum Scope {
  ReadCases = "read:cases",
  WriteCases = "write:cases",
  DeleteCases = "delete:cases",
  ReadAccount = "read:account",
  UpdateAccount = "update:account",
  DeleteAccount = "delete:account",
  ReadTemplates = "read:templates",
  WriteTemplates = "write:templates",
  UpdateTemplates = "update:templates",
  DeleteTemplates = "delete:templates",
  ReadFinancials = "read:financials",
  UpdateFinancials = "update:financials",
  CreateInvitations = "create:invitations",
  ReadInvitations = "read:invitations",
  DeleteInvitations = "delete:invitations",
  CreateMembers = "create:members",
  ReadMembers = "read:members",
  UpdateMembers = "update:members",
  DeleteMembers = "delete:members",
  ReadOrganisations = "read:organisations",
  UpdateOrganisations = "update:organisations",
  ListOrganisations = "list:organisations"
}

export type ScopeMap = { [id in Scope]: boolean };

/** This hook will fetch the current users permission scopes and returns a map. If a scope has the value of true in the map,
 *  then the user has permission to use that scope. */
export const usePermissions = (): ScopeMap & { isLoading: boolean } => {
  const { getAccessTokenSilently } = useAuth0();
  const [permissions, setPermissions] = useState<ScopeMap>(() =>
    Object.values(Scope).reduce((acc, scope) => ({ ...acc, [scope]: false }), {} as ScopeMap)
  );
  const [isLoading, setIsLoading] = useState<boolean>(true);

  useEffect(() => {
    const getPermissions = async () => {
      try {
        const accessToken = await getAccessTokenSilently();

        const decoded = jwtDecode<
          JwtPayload & {
            permissions: string[];
          }
        >(accessToken);

        setPermissions(
          Object.values(Scope).reduce((acc, scope) => ({ ...acc, [scope]: decoded.permissions.includes(scope) }), {} as ScopeMap)
        );
      } catch (_) {
        // Ignore access token errors. Auth0 will handle re-fetching a valid token.
      }
      setIsLoading(false);
    };
    getPermissions();
  }, [getAccessTokenSilently, setPermissions]);

  return { ...permissions, isLoading };
};
